import { Ref, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { webSocketStore } from 'stores/web-socket-store'
import { TitlesBuzzSentiment } from 'boot/axios'
import { BuzzSentiment } from '@stockpulse/typescript-axios/types/buzz-sentiment'
import { Auxiliary } from 'src/helper/Auxiliary'
import { useBufferedThrottleFn } from 'src/helper/BufferedThrottle'

export interface TitlesLiveData {
  id: number
  buzz: number,
  sentiment: number,
  sentimentIcon: string,
  sentimentIconColor: string,
  sentimentScore: number,
  time: number
}

export interface TitlesLiveDataCollection {[key:number]: TitlesLiveData}

export type SubscriptionId = string

export interface WsSubscription {
  titleIds:number[],
  subscriptionId: SubscriptionId
}

export const useRealTimeDataStore = defineStore('realTimeDataStore', () => {
  const webSocket = webSocketStore()
  const { isSocketReady } = storeToRefs(webSocket)

  const titlesRealtimeData: Ref<TitlesLiveDataCollection> = ref({})
  const pendingSubscriptions : Ref<WsSubscription[]> = ref([])
  const currentSubscriptions : Ref<WsSubscription[]> = ref([])

  const onWebsocketOpen = (isOpen: boolean) => {
    if (isOpen) {
      emitSubscribeToUnprocessedTitles()
    }
  }
  watch(isSocketReady, onWebsocketOpen, { immediate: true })

  const bufferedTitleBuzzSentimentUpdate = (newValues: BuzzSentiment[]) => {
    const reducedBuffer = newValues.reduce((acc: TitlesBuzzSentiment, curr : BuzzSentiment) : TitlesBuzzSentiment => {
      acc[curr.id] = curr
      return acc
    }, {} as TitlesBuzzSentiment)

    const workingValues = titlesRealtimeData.value

    for (const entryKey of Object.keys(reducedBuffer).map(Number)) {
      const buzzSentiment = reducedBuffer[entryKey]
      const sentimentScore = Auxiliary.calculateSentimentScore(buzzSentiment.s || 0) / 100
      const sentimentIcon = Auxiliary.getIconClassBySentimentValue(sentimentScore)
      const sentimentIconColor = Auxiliary.getColorForSentimentScore(sentimentScore)

      workingValues[entryKey] = {
        id: buzzSentiment.id,
        buzz: buzzSentiment.b,
        sentiment: buzzSentiment.s || 0,
        sentimentIcon,
        sentimentIconColor,
        sentimentScore,
        time: buzzSentiment.t
      }
    }
    titlesRealtimeData.value = workingValues
  }

  const throttleUpdateTitleBuzzSentiment = useBufferedThrottleFn(bufferedTitleBuzzSentimentUpdate, 3000)

  function emitSubscribeToUnprocessedTitles () {
    if (pendingSubscriptions.value.length === 0) {
      return
    }
    pendingSubscriptions.value.forEach(add => subscribeTitleUpdates(add.titleIds))
    pendingSubscriptions.value = []
  }

  function subscribeTitleUpdates (titleIds: number[]): SubscriptionId {
    const subscriptionId = generateSubscriptionId()
    if (!isSocketReady.value) {
      pendingSubscriptions.value.push({ titleIds, subscriptionId })
      return subscriptionId
    }

    webSocket.emitSubscribeTitles(titleIds, handleWebsocketTitleUpdateMessage, websocketSubscribeTitleIdsCallback)

    currentSubscriptions.value.push({ titleIds, subscriptionId })
    return subscriptionId
  }

  function generateSubscriptionId (): SubscriptionId {
    while (true) {
      const timestamp = new Date().getTime()
      const randomNum = Math.floor(Math.random() * 1000000)
      const subscriptionId = `${timestamp}-${randomNum}`
      const found = currentSubscriptions.value.findIndex(sub => sub.subscriptionId === subscriptionId) !== -1 ||
        pendingSubscriptions.value.findIndex(sub => sub.subscriptionId === subscriptionId) !== -1
      if (!found) {
        return subscriptionId
      }
    }
  }

  function unsubscribeTitleUpdates (subscriptionIds: SubscriptionId[]) {
    subscriptionIds.forEach(subscriptionId => {
      const wsPendingSubscriptionIndex = pendingSubscriptions.value.findIndex(ws => ws.subscriptionId === subscriptionId)
      if (wsPendingSubscriptionIndex !== -1) {
        pendingSubscriptions.value.slice(wsPendingSubscriptionIndex, 1)
      }

      const wsSubscriptionIndex = currentSubscriptions.value.findIndex(ws => ws.subscriptionId === subscriptionId)
      if (wsSubscriptionIndex === -1) {
        return
      }
      const currentTitleIds = getUniqueTitleIds(currentSubscriptions.value)

      currentSubscriptions.value.splice(wsSubscriptionIndex, 1)

      const remainingTitleIds = getUniqueTitleIds(currentSubscriptions.value)

      const titleIdsToUnsubscribe = currentTitleIds.filter(curTitle => !remainingTitleIds.includes(curTitle))

      webSocket.emitUnsubscribeTitles(titleIdsToUnsubscribe)
    })
  }

  function getUniqueTitleIds (wsRequests: WsSubscription[]): number[] {
    const uniqueIdsSet = new Set<number>()
    wsRequests.forEach(wsRequest => {
      wsRequest.titleIds.forEach(id => uniqueIdsSet.add(id))
    })
    return Array.from(uniqueIdsSet)
  }

  function websocketSubscribeTitleIdsCallback (error: unknown, data: unknown):void {
    if (error) {
      return
    }
    let dataArray : Array<BuzzSentiment>
    if (!Array.isArray(data)) {
      dataArray = [data as BuzzSentiment]
    } else {
      dataArray = data as BuzzSentiment[]
    }

    throttleUpdateTitleBuzzSentiment(dataArray)
  }

  function handleWebsocketTitleUpdateMessage (data: unknown, error: unknown):void {
    if (error) {
      return
    }
    throttleUpdateTitleBuzzSentiment([data as BuzzSentiment])
  }

  return {
    titlesRealtimeData,
    subscribeTitleUpdates,
    unsubscribeTitleUpdates
  }
})
